import { useMemo } from "react";
import type {
  MediaMetadata,
  PlatformRequirements,
} from "../components/brandDetails/tabs/creator-studio/platforms/types";

const REQUIREMENTS: PlatformRequirements = {
  maxImages: 4,
  maxVideos: 1,
  imageFormats: "JPEG, PNG, WebP, GIF",
  videoFormats: "MP4, MOV",
  maxImageSize: "5 MB (images), 15 MB (GIFs)",
  maxVideoSize: "512 MB",
  videoDuration: "2 min 20 sec",
  recommended: "1200 × 675 px (16:9)",
  minDimensions: "4 × 4 px",
  maxDimensions: "8192 × 8192 px",
  aspectRatios: "Any ratio (1:3 to 3:1, auto-cropped)",
  minWidth: 4,
  minHeight: 4,
  maxWidth: 8192,
  maxHeight: 8192,
};

export interface TwitterFormValidation {
  imageCountValid: boolean | null;
  imageSizeValid: boolean | null;
  dimensionsValid: boolean | null;
  aspectRatiosValid: boolean | null;
  videoCountValid: boolean | null;
  videoDurationValid: boolean | null;
}

export function useTwitterForm(mediaMetadata: MediaMetadata[]) {
  const maxImages =
    typeof REQUIREMENTS.maxImages === "number" ? REQUIREMENTS.maxImages : 10;

  const validation = useMemo((): TwitterFormValidation => {
    const enabledMedia = mediaMetadata.slice(0, maxImages);

    if (enabledMedia.length === 0) {
      return {
        imageCountValid: null,
        imageSizeValid: null,
        dimensionsValid: null,
        aspectRatiosValid: null,
        videoCountValid: null,
        videoDurationValid: null,
      };
    }

    const images = enabledMedia.filter((m) => m.type === "image");
    const videos = enabledMedia.filter((m) => m.type === "video");

    const imageCountValid =
      images.length === 0 ? null : images.length <= maxImages;

    const maxImageSizeBytes = 5 * 1024 * 1024;
    const imageSizeValid =
      images.length === 0
        ? null
        : images.every((img) => img.size <= maxImageSizeBytes);

    const dimensionsValid =
      images.length === 0
        ? null
        : images.every(
            (img) =>
              img.width >= (REQUIREMENTS.minWidth ?? 0) &&
              img.height >= (REQUIREMENTS.minHeight ?? 0) &&
              img.width <= (REQUIREMENTS.maxWidth ?? Infinity) &&
              img.height <= (REQUIREMENTS.maxHeight ?? Infinity)
          );

    const aspectRatiosValid: boolean | null = null;

    const maxVideos =
      typeof REQUIREMENTS.maxVideos === "number"
        ? REQUIREMENTS.maxVideos
        : Infinity;
    const videoCountValid =
      videos.length === 0 ? null : videos.length <= maxVideos;

    const videoDurationValid =
      videos.length === 0
        ? null
        : videos.every((video) => (video.duration ?? 0) <= 140);

    return {
      imageCountValid,
      imageSizeValid,
      dimensionsValid,
      aspectRatiosValid,
      videoCountValid,
      videoDurationValid,
    };
  }, [mediaMetadata, maxImages]);

  return { validation, requirements: REQUIREMENTS };
}
